/**
 * Remote video stall detection and recovery, platform-free core.
 *
 * 2026-09-27, Kuwait demo pair: the interpreter's browser kept receiving the
 * client's video (1.8 Mbps, no loss) and the bridge kept forwarding it, yet
 * the picture stayed frozen until hang-up. The stall was inside the receiving
 * page: the decoder stopped producing frames, or the <video> element stopped
 * presenting them. Nothing noticed, nothing retried, and the frozen frame
 * looked live.
 *
 * One monitor watches one remote video. Platform code feeds it a sample every
 * few seconds (inbound-rtp counters and, on web, the element) and performs the
 * recovery steps it asks for. Each stall runs its ladder once, each step once;
 * a pane runs at most MAX_REMOTE_VIDEO_STALLS_PER_PANE ladders.
 */
export const REMOTE_VIDEO_STALL_POLL_MS = 2000;
/** Frames or bytes arrive, but the picture has not moved for this long. */
export const REMOTE_VIDEO_STALL_AFTER_MS = 3000;
export const MAX_REMOTE_VIDEO_STALLS_PER_PANE = 4;
/**
 * How long a step gets to move the picture before the next one runs. A
 * keyframe travels bridge → sender → bridge → receiver, so it waits longest.
 */
export const REMOTE_VIDEO_STALL_STEP_WAIT_MS = {
    keyframe: 5000,
    play: 3000,
    reattach: 3000
};
/**
 * A step that cannot help a stall of that kind is not run: a decoder waiting
 * for a keyframe gains nothing from play(), and a keyframe cannot restart an
 * element that stopped presenting decoded frames.
 */
const LADDERS = {
    decode: ['keyframe', 'reattach'],
    paused: ['play', 'reattach'],
    render: ['play', 'reattach']
};
/**
 * A finite number.
 *
 * @param {unknown} value - A counter as the browser reported it.
 * @returns {boolean}
 */
function isNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}
/**
 * Whether a counter grew; undefined when either reading is missing.
 *
 * @param {unknown} previous - The earlier reading.
 * @param {unknown} next - The later reading.
 * @returns {boolean|undefined}
 */
function increased(previous, next) {
    return isNumber(previous) && isNumber(next) ? next > previous : undefined;
}
/**
 * How much a counter grew, never negative; undefined when a reading is missing.
 *
 * @param {unknown} previous - The earlier reading.
 * @param {unknown} next - The later reading.
 * @returns {number|undefined}
 */
function delta(previous, next) {
    return isNumber(previous) && isNumber(next) ? Math.max(0, next - previous) : undefined;
}
/**
 * Whether decoding or presenting moved between two samples of the same
 * element. Without requestVideoFrameCallback, currentTime stands in.
 *
 * @param {IRemoteVideoSample} previous - The earlier sample.
 * @param {IRemoteVideoSample} next - The later sample.
 * @returns {IProgress}
 */
function progressBetween(previous, next) {
    const before = previous.inbound;
    const after = next.inbound;
    const sameElement = previous.element && next.element && previous.element.elementId === next.element.elementId;
    let present;
    if (sameElement && previous.element && next.element) {
        present = isNumber(previous.element.presentedFrames) && isNumber(next.element.presentedFrames)
            ? next.element.presentedFrames > previous.element.presentedFrames
            : increased(previous.element.currentTime, next.element.currentTime);
    }
    return {
        arrival: after && before
            ? Boolean(increased(before.bytesReceived, after.bytesReceived)
                || increased(before.framesReceived, after.framesReceived))
            : undefined,
        decode: after && before ? increased(before.framesDecoded, after.framesDecoded) : undefined,
        frames: after && before
            ? increased(before.framesReceived, after.framesReceived)
                ?? increased(before.bytesReceived, after.bytesReceived)
            : undefined,
        present
    };
}
/**
 * The picture moved: presented frames where there is an element to watch,
 * decoded frames where there is not (native). A decode stall needs decoded
 * frames as well: currentTime alone is not proof the decoder restarted.
 *
 * @param {IProgress} progress - Movement in the latest interval.
 * @param {IRemoteVideoSample} sample - The latest sample.
 * @param {RemoteVideoStallKind} [kind] - The stall being recovered, if any.
 * @returns {boolean}
 */
function outputMoved(progress, sample, kind) {
    if (kind === 'decode' && progress.decode !== true) {
        return false;
    }
    if (!sample.element) {
        return progress.decode === true;
    }
    if (sample.element.paused) {
        return false;
    }
    if (progress.present !== undefined) {
        return progress.present;
    }
    // Presentation unknown (the element was just replaced): an element-side
    // stall waits for the next interval, a decode stall accepts decoding.
    return kind === 'decode';
}
/**
 * Watches one remote video and drives its recovery ladder.
 *
 * @param {IRemoteVideoStallMonitorOptions} options - Step runner and reporter.
 * @returns {Object}
 */
export function createRemoteVideoStallMonitor(options) {
    let previous;
    let lastDecodeAt = 0;
    let lastOutputAt = 0;
    let pausedSince;
    let ladder;
    let failed;
    let ladders = 0;
    let disposed = false;
    const finish = (current, outcome, sample) => {
        const before = current.start.inbound;
        const after = sample.inbound;
        const bytes = delta(before?.bytesReceived, after?.bytesReceived);
        options.report({
            bytesReceivedDeltaKb: bytes === undefined ? undefined : Math.round(bytes / 1024),
            error_name: current.errorName,
            framesDecodedDelta: delta(before?.framesDecoded, after?.framesDecoded),
            framesDroppedDelta: delta(before?.framesDropped, after?.framesDropped),
            framesReceivedDelta: delta(before?.framesReceived, after?.framesReceived),
            freezeCountDelta: delta(before?.freezeCount, after?.freezeCount),
            keyFramesDecodedDelta: delta(before?.keyFramesDecoded, after?.keyFramesDecoded),
            kind: current.kind,
            outcome,
            paneRole: options.paneRole,
            paused: sample.element?.paused,
            readyState: sample.element?.readyState,
            stall: current.index,
            stallMs: Math.max(0, sample.at - current.lastOutputAt),
            step: current.steps[current.steps.length - 1],
            steps: current.steps.join(',')
        });
    };
    /**
     * The next step for this stall, planned from what it looks like now: a
     * paused element that plays again but still decodes nothing has become
     * a decode stall.
     *
     * @param {ILadder} current - The running ladder.
     * @param {RemoteVideoStallKind} kind - The stall as it looks now.
     * @returns {RemoteVideoStallStep|undefined}
     */
    const nextStep = (current, kind) => LADDERS[kind].find(step => !current.steps.includes(step) && options.can(step));
    const runStep = (current, step, at) => {
        current.steps.push(step);
        current.stepAt = at;
        options.act(step);
    };
    const classify = (sample, progress) => {
        const { at, element } = sample;
        if (pausedSince !== undefined && at - pausedSince >= REMOTE_VIDEO_STALL_AFTER_MS) {
            return 'paused';
        }
        // Without bytes or frames arriving there is nothing to decode: the
        // sender or the bridge stopped, which the track streaming status shows.
        if (progress.arrival !== true) {
            return undefined;
        }
        if (progress.frames === true && at - lastDecodeAt >= REMOTE_VIDEO_STALL_AFTER_MS) {
            return 'decode';
        }
        // Frames decode but none reach the screen. An element that reports
        // neither presented frames nor currentTime cannot be judged, only
        // its pause.
        if (element && !element.paused && progress.present !== undefined && progress.decode === true
            && at - lastOutputAt >= REMOTE_VIDEO_STALL_AFTER_MS) {
            return 'render';
        }
        return undefined;
    };
    const observe = (sample) => {
        if (disposed) {
            return { stalled: false };
        }
        if (!sample.visible) {
            // Judged again from a fresh baseline when the page shows.
            if (ladder) {
                finish(ladder, 'hidden', sample);
                ladder = undefined;
            }
            failed = undefined;
            previous = undefined;
            pausedSince = undefined;
            return { stalled: false };
        }
        const element = sample.element;
        if (element?.hasStream && element.paused) {
            pausedSince = pausedSince ?? sample.at;
        }
        else {
            pausedSince = undefined;
        }
        if (!previous) {
            previous = sample;
            lastDecodeAt = sample.at;
            lastOutputAt = sample.at;
            return { stalled: false };
        }
        const progress = progressBetween(previous, sample);
        const newElement = element && previous.element?.elementId !== element.elementId;
        previous = sample;
        if (progress.decode !== false) {
            // Unknown (no stats) is not evidence of a stuck decoder.
            lastDecodeAt = sample.at;
        }
        if (outputMoved(progress, sample) || newElement) {
            // A fresh element starts its own clock; only movement recovers.
            lastOutputAt = sample.at;
        }
        if (ladder) {
            const current = ladder;
            if (outputMoved(progress, sample, current.kind) && sample.at > current.stepAt) {
                // Any movement after the step started came after it: the
                // picture was standing still until then.
                finish(current, 'recovered', sample);
                ladder = undefined;
                return { stalled: false };
            }
            const step = current.steps[current.steps.length - 1];
            if (sample.at - current.stepAt < REMOTE_VIDEO_STALL_STEP_WAIT_MS[step]) {
                return { stalled: true };
            }
            const next = nextStep(current, classify(sample, progress) || current.kind);
            if (next) {
                runStep(current, next, sample.at);
                return { stalled: true };
            }
            finish(current, 'unrecovered', sample);
            ladder = undefined;
            failed = current;
            return { stalled: true };
        }
        if (failed) {
            if (outputMoved(progress, sample, failed.kind)) {
                finish(failed, 'resumed', sample);
                failed = undefined;
                return { stalled: false };
            }
            return { stalled: true };
        }
        const kind = classify(sample, progress);
        if (!kind) {
            return { stalled: false };
        }
        if (ladders >= MAX_REMOTE_VIDEO_STALLS_PER_PANE) {
            // Out of attempts: still honest on screen, but no more steps or
            // reports for this pane.
            return { stalled: true };
        }
        ladders += 1;
        const started = {
            index: ladders,
            kind,
            lastOutputAt,
            start: sample,
            stepAt: sample.at,
            steps: []
        };
        const first = nextStep(started, kind);
        if (!first) {
            finish(started, 'unrecovered', sample);
            failed = started;
            return { stalled: true };
        }
        ladder = started;
        runStep(started, first, sample.at);
        return { stalled: true };
    };
    return {
        /**
         * The pane or its track went away. A ladder still running is reported.
         *
         * @param {number} at - Current time.
         * @returns {void}
         */
        dispose(at) {
            if (ladder && previous) {
                finish(ladder, 'gone', {
                    ...previous,
                    at
                });
            }
            ladder = undefined;
            failed = undefined;
            disposed = true;
        },
        /**
         * A step failed (play() rejected): its error name goes on the report.
         *
         * @param {string} errorName - DOMException name.
         * @returns {void}
         */
        noteStepError(errorName) {
            if (ladder && !ladder.errorName) {
                ladder.errorName = errorName;
            }
        },
        observe
    };
}
